/**
 * `Presence` — the one place a conditional subtree is allowed to animate OUT.
 *
 *   <Presence>{open && <Dialog key="dialog" />}</Presence>
 *
 * Children must carry a stable `key` and their root must be a motion element
 * with an `exit` variant (see ./overlay, ./presets). Portals are fine: the
 * presence signal travels through React context, which crosses a portal.
 *
 * `mode`:
 *   sync       enter and exit overlap (overlays, list rows)
 *   wait       the outgoing one finishes first (pages, wizard steps, state
 *              switches — anything that occupies the same box)
 */
import React from 'react';
import { AnimatePresence } from 'framer-motion';

interface PresenceProps {
  children: React.ReactNode;
  mode?: 'sync' | 'wait' | 'popLayout';
  /** Animate on first mount too. Off for content that is already there when
   * the screen opens — a route's initial paint should not replay a page. */
  initial?: boolean;
  /** Passed to the variants as `custom` (direction, index…). */
  custom?: unknown;
  /** Called once every exit animation has finished. */
  onExitComplete?: () => void;
}

export const Presence: React.FC<PresenceProps> = ({
  children,
  mode = 'sync',
  initial = true,
  custom,
  onExitComplete,
}) => (
  <AnimatePresence
    mode={mode}
    initial={initial}
    custom={custom}
    onExitComplete={onExitComplete}
  >
    {children}
  </AnimatePresence>
);
